import React, { useEffect, useState } from 'react';
import { useCustomerStore } from '../../stores/customerStore';
import { useProjectStore } from '../../stores/projectStore';
import { Customer, CustomerStatus, CustomerStats } from '../../types';
import CustomerDetailDialog from './CustomerDetailDialog';
import EditCustomerDialog from './EditCustomerDialog';
import NewCustomerDialog from './NewCustomerDialog';
import './CustomersView.css';

// Farba vlákna (slovenský názov) -> hex pre farebnú bodku
const FIBER_HEX: { [key: string]: string } = {
  'modrá': '#2196F3', 'oranžová': '#FF9800', 'zelená': '#4CAF50', 'hnedá': '#795548',
  'šedá': '#9E9E9E', 'biela': '#FAFAFA', 'červená': '#F44336', 'čierna': '#212121',
  'žltá': '#FFEB3B', 'fialová': '#9C27B0', 'ružová': '#E91E63', 'tyrkysová': '#009688',
};
const fiberColorHex = (name?: string): string => FIBER_HEX[(name || '').toLowerCase()] || '#CBD5E1';

// Dlaždice so štatistikou. Každá je zároveň filter — technikovi stačí kliknúť
// na „Čakajú na inštaláciu" a zoznam sa zúži, netreba hľadať v rozbaľovačke.
const STAT_KARTY: {
  stav: CustomerStatus | 'all';
  popis: string;
  farba: string;
  hodnota: (s: CustomerStats) => number;
}[] = [
  { stav: 'all', popis: 'Celkom', farba: '', hodnota: (s) => s.total },
  { stav: 'active' as CustomerStatus, popis: 'Aktívni', farba: 'green', hodnota: (s) => s.active },
  { stav: 'pending_installation' as CustomerStatus, popis: 'Čakajú na inštaláciu', farba: 'blue', hodnota: (s) => s.pending_installation },
  { stav: 'suspended' as CustomerStatus, popis: 'Pozastavení', farba: 'yellow', hodnota: (s) => s.suspended },
  { stav: 'terminated' as CustomerStatus, popis: 'Ukončení', farba: 'red', hodnota: (s) => s.terminated },
];

const CustomersView: React.FC = () => {
  const { customers, stats, isLoading, error, fetchCustomers, fetchStats, deleteCustomer, activateCustomer, suspendCustomer, terminateCustomer } = useCustomerStore();
  const { projects, currentProject, fetchProjects } = useProjectStore();
  
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState<CustomerStatus | 'all'>('all');
  // 'all' = zákazníci zo všetkých projektov, na ktoré má používateľ právo.
  // Filtruje SERVER, nie prehliadač — inak by po sieti chodili dáta, ktoré aj
  // tak nikto neuvidí, a štatistiky hore by nesedeli so zoznamom.
  const [projectFilter, setProjectFilter] = useState<number | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Zoznam projektov do výberu; keď ešte nie je načítaný, dotiahneme ho
  useEffect(() => {
    if (projects.length === 0) {
      fetchProjects();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Jeden efekt na oba filtre. Predtým boli dva a oba bežali pri otvorení
  // karty, takže sa zoznam aj štatistiky sťahovali dvakrát.
  useEffect(() => {
    const status = statusFilter === 'all' ? undefined : statusFilter;
    const projekt = projectFilter === 'all' ? undefined : projectFilter;
    fetchCustomers(status, projekt);
    fetchStats(projekt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, projectFilter]);

  const handleViewDetail = (customer: Customer) => {
    setSelectedCustomer(customer);
    setDetailDialogOpen(true);
  };

  const handleEdit = (customer: Customer) => {
    setSelectedCustomer(customer);
    setEditDialogOpen(true);
  };

  const handleDelete = async (customerId: number) => {
    if (window.confirm('Naozaj chcete zmazať tohto zákazníka?')) {
      try {
        await deleteCustomer(customerId);
        alert('Zákazník bol úspešne zmazaný');
      } catch (error) {
        alert('Chyba pri mazaní zákazníka');
      }
    }
  };

  const handleActivate = async (customerId: number) => {
    try {
      await activateCustomer(customerId);
      alert('Zákazník bol aktivovaný');
    } catch (error) {
      alert('Chyba pri aktivácii zákazníka');
    }
  };

  const handleSuspend = async (customerId: number) => {
    if (window.confirm('Naozaj chcete pozastaviť službu tohto zákazníka?')) {
      try {
        await suspendCustomer(customerId);
        alert('Služba zákazníka bola pozastavená');
      } catch (error) {
        alert('Chyba pri pozastavení služby');
      }
    }
  };

  const handleTerminate = async (customerId: number) => {
    if (window.confirm('Naozaj chcete ukončiť službu tohto zákazníka? Táto akcia je nevratná.')) {
      try {
        await terminateCustomer(customerId);
        alert('Služba zákazníka bola ukončená');
      } catch (error) {
        alert('Chyba pri ukončení služby');
      }
    }
  };



  const getStatusBadge = (status: CustomerStatus) => {
    const badges = {
      active: { label: 'Aktívny', className: 'active' },
      suspended: { label: 'Pozastavený', className: 'suspended' },
      terminated: { label: 'Ukončený', className: 'terminated' },
      pending_installation: { label: 'Čaká na inštaláciu', className: 'pending' },
    };
    const badge = badges[status];
    return (
      <span className={`status-badge ${badge.className}`}>
        {badge.label}
      </span>
    );
  };

  const getCustomerName = (customer: Customer) => {
    if (customer.customer_type === 'company') {
      return customer.company_name || 'Bez názvu';
    }
    return `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || 'Bez mena';
  };

  // Filter customers by search query
  const filteredCustomers = customers.filter(customer => {
    const name = getCustomerName(customer).toLowerCase();
    const email = customer.email.toLowerCase();
    const phone = customer.phone.toLowerCase();
    const customerNumber = customer.customer_number.toLowerCase();
    const pole = (customer.connected_pole_name || '').toLowerCase();
    const splitter = (customer.splitter || '').toLowerCase();
    const query = searchQuery.toLowerCase();

    return name.includes(query) || email.includes(query) || phone.includes(query)
      || customerNumber.includes(query) || pole.includes(query) || splitter.includes(query);
  });

  return (
    <div className="customers-view">
      {/* Header */}
      <div className="customers-header">
        <h1>👥 Zákazníci</h1>
        <button onClick={() => setNewDialogOpen(true)} className="btn-primary">
          ➕ Nový zákazník
        </button>
      </div>

      {/* Stats — každá dlaždica je zároveň filter na ten stav.
          Druhý klik na tú istú dlaždicu filter zruší. */}
      <div className="stats-grid">
        {STAT_KARTY.map(({ stav, popis, farba, hodnota }) => {
          const aktivna = statusFilter === stav;
          return (
            <button
              key={String(stav)}
              type="button"
              className={`stat-card ${farba} clickable ${aktivna ? 'active' : ''}`}
              aria-pressed={aktivna}
              title={
                stav === 'all'
                  ? 'Zobraziť všetkých zákazníkov'
                  : aktivna
                    ? 'Kliknutím filter zrušíš'
                    : `Zobraziť len: ${popis}`
              }
              onClick={() => setStatusFilter(aktivna ? 'all' : stav)}
            >
              <div className="stat-label">{popis}</div>
              <div className="stat-value">{hodnota(stats)}</div>
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="filters-container">
        <div className="filters-grid">
          <div className="filter-group">
            <label>Stav</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as CustomerStatus | 'all')}
            >
              <option value="all">🔍 Všetky statusy</option>
              <option value="active">✅ Aktívni</option>
              <option value="pending_installation">⏳ Čakajú na inštaláciu</option>
              <option value="suspended">⏸️ Pozastavení</option>
              <option value="terminated">❌ Ukončení</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Projekt</label>
            <select
              value={projectFilter}
              onChange={(e) =>
                setProjectFilter(e.target.value === 'all' ? 'all' : Number(e.target.value))
              }
            >
              <option value="all">📂 Všetky projekty</option>
              {currentProject && (
                <option value={currentProject.id}>📌 {currentProject.name} (otvorený)</option>
              )}
              {[...projects]
                .filter((p) => p.id !== currentProject?.id)
                .sort((a, b) => a.name.localeCompare(b.name, 'sk'))
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
            </select>
          </div>
          <div className="filter-group">
            <label>Vyhľadávanie</label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Meno, email, telefón, číslo, stĺpik..."
            />
          </div>
        </div>
        <div className="results-count">
          Zobrazených: {filteredCustomers.length} z {customers.length}
          {projectFilter !== 'all' && (
            <>
              {' '}v projekte{' '}
              <b>{projects.find((p) => p.id === projectFilter)?.name || `#${projectFilter}`}</b>
            </>
          )}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="loading-state">
          Načítavam zákazníkov...
        </div>
      )}

      {/* Customers List */}
      {!isLoading && filteredCustomers.length === 0 && (
        <div className="empty-state">
          Žiadni zákazníci
        </div>
      )}

      {!isLoading && filteredCustomers.length > 0 && (
        <div className="customers-table-container">
          <table className="customers-table">
            <thead>
              <tr>
                <th>Číslo</th>
                <th>Projekt</th>
                <th>Meno/Firma</th>
                <th>Kontakt</th>
                <th>Pripojenie</th>
                <th>Služba</th>
                <th>Stav</th>
                <th>Akcie</th>
              </tr>
            </thead>
            <tbody>
              {filteredCustomers.map(customer => (
                <tr key={customer.id}>
                  <td>{customer.customer_number}</td>
                  <td style={{ fontSize: '13px', color: '#64748b' }}>
                    {customer.project_name || '—'}
                  </td>
                  <td>
                    <div
                      style={{
                        fontWeight: 500,
                        color: '#4CAF50',
                        cursor: 'pointer',
                        textDecoration: 'underline'
                      }}
                      onClick={() => handleViewDetail(customer)}
                      title="Kliknite pre zobrazenie detailu"
                    >
                      {getCustomerName(customer)}
                    </div>
                    <div style={{ fontSize: '13px', color: '#64748b' }}>{customer.city}</div>
                  </td>
                  <td>
                    <div>{customer.email}</div>
                    <div style={{ fontSize: '13px', color: '#64748b' }}>{customer.phone}</div>
                  </td>
                  <td>
                    {customer.connected_pole_name ? (
                      <>
                        <div style={{ fontWeight: 500 }}>📍 {customer.connected_pole_name}</div>
                        <div style={{ fontSize: '13px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span>{customer.splitter} · výstup {customer.output_number}</span>
                          {customer.fiber_color && (
                            <>
                              <span style={{
                                display: 'inline-block', width: '12px', height: '12px', borderRadius: '50%',
                                backgroundColor: fiberColorHex(customer.fiber_color),
                                border: '1px solid rgba(0,0,0,0.25)'
                              }} />
                              <span>{customer.fiber_color}</span>
                            </>
                          )}
                        </div>
                      </>
                    ) : (
                      <span style={{ color: '#cbd5e1' }}>—</span>
                    )}
                  </td>
                  <td>{customer.service_speed || customer.service_type}</td>
                  <td>{getStatusBadge(customer.status)}</td>
                  <td>
                    <div className="action-buttons">
                      <button onClick={() => handleViewDetail(customer)} className="action-btn" title="Detail">
                        👁️
                      </button>
                      <button onClick={() => handleEdit(customer)} className="action-btn" title="Upraviť">
                        ✏️
                      </button>
                      {customer.status === 'pending_installation' && (
                        <button onClick={() => handleActivate(customer.id)} className="action-btn" title="Aktivovať">
                          ✅
                        </button>
                      )}
                      {customer.status === 'active' && (
                        <button onClick={() => handleSuspend(customer.id)} className="action-btn" title="Pozastaviť">
                          ⏸️
                        </button>
                      )}
                      {(customer.status === 'active' || customer.status === 'suspended') && (
                        <button onClick={() => handleTerminate(customer.id)} className="action-btn" title="Ukončiť">
                          ❌
                        </button>
                      )}
                      <button onClick={() => handleDelete(customer.id)} className="action-btn" title="Zmazať">
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Dialogs */}
      {selectedCustomer && (
        <>
          <CustomerDetailDialog
            customer={selectedCustomer}
            open={detailDialogOpen}
            onClose={() => setDetailDialogOpen(false)}
            onRefresh={() => {
              fetchCustomers(statusFilter === 'all' ? undefined : statusFilter);
              fetchStats();
            }}
            onEdit={() => {
              setDetailDialogOpen(false);
              setEditDialogOpen(true);
            }}
            onDelete={handleDelete}
            onTerminate={handleTerminate}
          />
          <EditCustomerDialog
            customer={selectedCustomer}
            open={editDialogOpen}
            onClose={() => setEditDialogOpen(false)}
          />
        </>
      )}
      <NewCustomerDialog
        open={newDialogOpen}
        onClose={() => setNewDialogOpen(false)}
      />
    </div>
  );
};

export default CustomersView;

